import React, { FC, useEffect, useState } from 'react';

import { Breadcrumbs } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { useMutation, useQuery } from 'react-apollo';
import { Link, useHistory } from 'react-router-dom';

import Button from 'Components-redesign/Button';
import ImageSelect from 'Components-redesign/Fields/ImageSelect';
import MultiSelect from 'Components-redesign/Fields/MultiSelect';
import { Input } from 'Components-redesign/Fields/New/Input';
import Textarea from 'Components-redesign/Fields/Textarea';
import Field from 'Components-redesign/Form/Field';
import Loader from 'Components-redesign/Loader';
import Modal from 'Components-redesign/Modal';
import ModalContentWrapper from 'Components-redesign/Modal/ModalContentWrapper';
import useModal from 'Components-redesign/Modal/useModal';
import Toggle from 'Components-redesign/Toggle';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useI18n } from 'Hooks';
import {
  AiQuery,
  AiQueryVariables,
  CreateRelease,
  DeleteRelease,
  GetAllCompaniesForReleases,
  GetRelease,
  UpdateRelease,
} from 'Scenes/Releases/api/__queries__';
import {
  AI_HELP,
  CREATE_RELEASE,
  DELETE_RELEASE,
  UPDATE_RELEASE,
} from 'Scenes/Releases/api/mutations';
import { GET_ALL_COMPANIES, GET_RELEASE } from 'Scenes/Releases/api/queries';
import { AI_CONTEXT } from 'Scenes/Releases/consts';
import { PRODUCT_TAG, RELEASE_TYPE } from 'Scenes/Releases/types';
import { Box } from 'styles/box';

import { DeleteWrapper, ReleaseNoteWrapper, ScrollableBox, Wrapper } from '../../styles';

interface Note {
  titleLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  showToCustomers: boolean;
  imageUrls?: string[];
  companyIds?: string[];
}

interface FormData {
  createdBy: string;
  titleLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  notes: Note[];
  productTags: PRODUCT_TAG[];
  repository: string;
  triggerModal: boolean;
}

interface Props {
  id?: string;
  username: string;
  refetch: () => void;
  onComplete: () => void;
}

/** Form for creating & editing a release with type RELEASE_NOTE */
const ReleaseNoteForm: FC<Props> = ({ id, username, refetch, onComplete }) => {
  const { t } = useI18n();

  const getLanguageName = (langCode: string): string => {
    const languageMap: Record<string, string> = {
      en: t('Companies.Form.Languages.English'),
      se: t('Companies.Form.Languages.Swedish'),
      es: t('Companies.Form.Languages.Spanish'),
      no: t('Companies.Form.Languages.Norwegian'),
      tr: t('Companies.Form.Languages.Turkish'),
      da: t('Companies.Form.Languages.Danish'),
      ar: t('Companies.Form.Languages.Arabic'),
    };

    return languageMap[langCode] || langCode;
  };

  const confirmDeleteModal = useModal();
  const history = useHistory();

  const [createRelease] = useMutation<CreateRelease>(CREATE_RELEASE);
  const [updateRelease] = useMutation<UpdateRelease>(UPDATE_RELEASE);
  const [deleteRelease] = useMutation<DeleteRelease>(DELETE_RELEASE);
  const [aiHelper] = useMutation<AiQuery, AiQueryVariables>(AI_HELP);

  const companiesQuery = useQuery<GetAllCompaniesForReleases>(GET_ALL_COMPANIES);
  const allCompanies = companiesQuery.data?.allCompanies || [];

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [loadingNoteLang, setLoadingNoteLang] = useState<Record<number, boolean>>({});
  const [noteTranslationModals, setNoteTranslationModals] = useState<Record<number, boolean>>({});
  const [loadingTitleLang, setLoadingTitleLang] = useState<boolean>(false);
  const [titleTranslationModal, setTitleTranslationModal] = useState<boolean>(false);
  const [companySearchTerms, setCompanySearchTerms] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState<FormData>({
    createdBy: username,
    titleLang: {
      en: '',
      se: '',
      es: '',
      no: '',
      tr: '',
      da: '',
      ar: '',
    },
    notes: [
      {
        titleLang: {
          en: '',
          se: '',
          es: '',
          no: '',
          tr: '',
          da: '',
          ar: '',
        },
        showToCustomers: false,
        imageUrls: [],
        companyIds: [],
      },
    ],
    productTags: [],
    repository: '',
    triggerModal: false,
  });

  const releaseQuery = useQuery<GetRelease>(GET_RELEASE, {
    variables: {
      id,
    },
    skip: !id || id === 'new',
  });

  const release = releaseQuery.data?.superAdminViewer.Release;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (id && id !== 'new') {
      /** Update */
      await updateRelease({
        variables: {
          input: {
            id,
            type: RELEASE_TYPE.RELEASE_NOTES,
            titleLang: formData.titleLang,
            notes: formData.notes,
            productTags: formData.productTags,
            repository: formData.repository,
            triggerModal: formData.triggerModal,
          },
        },
      });
    } else {
      /** Create */
      try {
        await createRelease({
          variables: {
            input: {
              createdBy: username,
              type: RELEASE_TYPE.RELEASE_NOTES,
              titleLang: formData.titleLang,
              notes: formData.notes,
              productTags: formData.productTags,
              imageUrls: [],
              repository: formData.repository,
              triggerModal: formData.triggerModal,
            },
          },
        });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      }
    }

    refetch();
    history.push('/releases');
    onComplete();
    setLoading(false);
  };

  const handleDelete = async () => {
    confirmDeleteModal.hide();

    setDeleteLoading(true);

    try {
      await deleteRelease({
        variables: {
          id,
        },
      });

      refetch();
      history.push('/releases');
    } catch (error) {
      console.error('Error deleting release:', error);
      setError(error);
    }

    setDeleteLoading(false);
  };

  useEffect(() => {
    if (release) {
      setFormData({
        titleLang: release.titleLang,
        notes: release.notes.map((note) => ({
          titleLang: note.titleLang,
          showToCustomers: note.showToCustomers,
          imageUrls: note.imageUrls || [],
          companyIds: note.companyIds || [],
        })),
        productTags: release.productTags,
        createdBy: release.createdBy,
        repository: release.repository || '',
        triggerModal: release.triggerModal || false,
      });
    }
  }, [release]);

  const addNote = () => {
    const newNote = {
      titleLang: {
        en: '',
        se: '',
        es: '',
        no: '',
        tr: '',
        da: '',
        ar: '',
      },
      showToCustomers: false,
      imageUrls: [],
      companyIds: [],
    };

    setFormData({
      ...formData,
      notes: [...formData.notes, newNote],
    });
  };

  const handleGenerateLang = async (noteIndex: number) => {
    setLoadingNoteLang({ ...loadingNoteLang, [noteIndex]: true });

    try {
      const res = await aiHelper({
        variables: {
          context: AI_CONTEXT,
          input: formData.notes[noteIndex].titleLang.en,
        },
      });

      const aiMessage = res.data?.generateCompletion.message;

      if (!aiMessage) {
        setError('AI response is empty or invalid');
        return;
      }

      const parsedMessage = JSON.parse(aiMessage);

      if (res.data) {
        const updatedNotes = [...formData.notes];
        updatedNotes[noteIndex] = {
          ...updatedNotes[noteIndex],
          titleLang: {
            ...updatedNotes[noteIndex].titleLang,
            se: parsedMessage.se,
            es: parsedMessage.es,
            no: parsedMessage.no,
            tr: parsedMessage.tr,
            da: parsedMessage.da,
            ar: parsedMessage.ar,
          },
        };

        setFormData({
          ...formData,
          notes: updatedNotes,
        });
      }
    } catch (error) {
      console.error('Error generating translations:', error);
      setError(error);
    } finally {
      setLoadingNoteLang({ ...loadingNoteLang, [noteIndex]: false });
    }
  };

  const toggleNoteTranslationModal = (noteIndex: number) => {
    setNoteTranslationModals({
      ...noteTranslationModals,
      [noteIndex]: !noteTranslationModals[noteIndex],
    });
  };

  const isNoteTranslationGenerated = (noteIndex: number): boolean => {
    const note = formData.notes[noteIndex];
    // If note is not shown to customers, translations are not required
    if (!note.showToCustomers) {
      return true;
    }
    return Object.values(note.titleLang).every((value) => value.trim());
  };

  const handleGenerateTitleLang = async () => {
    setLoadingTitleLang(true);

    try {
      const res = await aiHelper({
        variables: {
          context: AI_CONTEXT,
          input: formData.titleLang.en,
        },
      });

      const aiMessage = res.data?.generateCompletion.message;

      if (!aiMessage) {
        setError('AI response is empty or invalid');
        return;
      }

      const parsedMessage = JSON.parse(aiMessage);

      if (res.data) {
        setFormData({
          ...formData,
          titleLang: {
            ...formData.titleLang,
            se: parsedMessage.se,
            es: parsedMessage.es,
            no: parsedMessage.no,
            tr: parsedMessage.tr,
            da: parsedMessage.da,
            ar: parsedMessage.ar,
          },
        });
      }
    } catch (error) {
      console.error('Error generating translations:', error);
      setError(error);
    } finally {
      setLoadingTitleLang(false);
    }
  };

  const toggleTitleTranslationModal = () => {
    setTitleTranslationModal(!titleTranslationModal);
  };

  const isTitleTranslationGenerated = (): boolean => {
    return Object.values(formData.titleLang).every((value) => value.trim());
  };

  const allNotesHaveTranslations = formData.notes.every((_, index) =>
    isNoteTranslationGenerated(index),
  );
  const allNotesHiddenFromCustomers = formData.notes.every((note) => !note.showToCustomers);

  const allTranslationsComplete =
    (isTitleTranslationGenerated() && allNotesHaveTranslations) || allNotesHiddenFromCustomers;

  // Create list of product tags from enum for MultiSelect
  const productTagOptions = Object.values(PRODUCT_TAG).map((tag) => ({
    id: tag,
    name: tag,
  }));

  const getAvailableCompaniesForNote = (noteIndex: number) => {
    const selectedIds = formData.notes[noteIndex].companyIds || [];
    return allCompanies
      .filter((c) => !selectedIds.includes(c.id))
      .sort((a, b) => (a.name > b.name ? 1 : -1));
  };

  const handleCompanyInputChange = (noteIndex: number, value: string) => {
    setCompanySearchTerms({ ...companySearchTerms, [noteIndex]: value });

    const match = allCompanies.find(
      (c) => c.name === value && !(formData.notes[noteIndex].companyIds || []).includes(c.id),
    );

    if (match) {
      const updatedNotes = [...formData.notes];
      updatedNotes[noteIndex] = {
        ...updatedNotes[noteIndex],
        companyIds: [...(updatedNotes[noteIndex].companyIds || []), match.id],
      };
      setFormData({ ...formData, notes: updatedNotes });
      setCompanySearchTerms({ ...companySearchTerms, [noteIndex]: '' });
    }
  };

  const removeCompanyFromNote = (noteIndex: number, companyId: string) => {
    const updatedNotes = [...formData.notes];
    updatedNotes[noteIndex] = {
      ...updatedNotes[noteIndex],
      companyIds: (updatedNotes[noteIndex].companyIds || []).filter((id) => id !== companyId),
    };
    setFormData({ ...formData, notes: updatedNotes });
  };

  const getCompanyName = (companyId: string): string => {
    return allCompanies.find((c) => c.id === companyId)?.name || companyId;
  };

  return (
    <>
      <Wrapper loading={deleteLoading}>
        <Breadcrumbs style={{ marginBottom: '8px' }} aria-label="breadcrumb">
          <Link to="/releases">{t('Releases.Breadcrumb')}</Link>
          <p>{release ? `"${t(release.titleLang)}"` : t('Releases.PublishReleaseNotes')}</p>
        </Breadcrumbs>

        {releaseQuery.loading ? (
          <Box
            display="flex"
            marginTop="xl"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="small">
            <Loader color="var(--colorBlack)" />
            <Paragraph level={3}>{t('Releases.ReleaseNoteForm.Loading')}</Paragraph>
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Heading level={1}>
                  {release
                    ? `${t('General.Edit')} ${t(release.titleLang)}`
                    : t('Releases.ReleaseNoteForm.PublishNew')}
                </Heading>
                {!release && <Paragraph level={3}>{t('Releases.ReleaseNoteForm.Desc')}</Paragraph>}
              </Box>

              {release && (
                <DeleteWrapper onClick={confirmDeleteModal.toggle}>
                  <Delete fontSize="large" htmlColor="var(--colorBlueGray900)" />
                </DeleteWrapper>
              )}
            </Box>

            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap="medium">
                <Field label={t('Releases.ReleaseNoteForm.Title')}>
                  <Input
                    type="text"
                    required={!allNotesHiddenFromCustomers}
                    value={formData.titleLang.en}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        titleLang: {
                          ...formData.titleLang,
                          en: e.target.value,
                        },
                      })
                    }
                    placeholder={t('Releases.ReleaseNoteForm.Title.Placeholder')}
                  />
                  {!allNotesHiddenFromCustomers && (
                    <Button
                      type="button"
                      text={
                        loadingTitleLang
                          ? `${t('General.Loading')}...`
                          : isTitleTranslationGenerated()
                          ? t('Releases.ViewEditLang')
                          : t('Releases.GenerateLang')
                      }
                      styleType={isTitleTranslationGenerated() ? 'Dark' : 'OK'}
                      disabled={loadingTitleLang || !formData.titleLang.en.trim()}
                      handleClick={() =>
                        isTitleTranslationGenerated()
                          ? toggleTitleTranslationModal()
                          : handleGenerateTitleLang()
                      }
                      height="40px"
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </Field>

                <Field label={t('Releases.ReleaseNoteForm.Repo')}>
                  <Input
                    type="text"
                    required
                    value={formData.repository}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        repository: e.target.value,
                      })
                    }
                    placeholder={t('Releases.ReleaseNoteForm.Repo.Placeholder')}
                  />
                </Field>

                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap="small">
                  {formData.notes.map((note, index) => (
                    <ReleaseNoteWrapper
                      key={index}
                      display="flex"
                      justifyContent="center"
                      alignItems="center">
                      <Field label={`${t('Releases.ReleaseNoteForm.Note')} #${index + 1}`}>
                        <Box display="flex" gap="small" alignItems="center">
                          <Input
                            type="text"
                            required
                            value={note.titleLang.en}
                            onChange={(e) => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes[index] = {
                                ...updatedNotes[index],
                                titleLang: {
                                  ...updatedNotes[index].titleLang,
                                  en: e.target.value,
                                },
                              };

                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}
                            placeholder={t('Releases.ReleaseNoteForm.Note.Placeholder')}
                            style={{ flexGrow: 1 }}
                          />
                          <DeleteWrapper
                            onClick={() => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes.splice(index, 1);

                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}>
                            <Delete fontSize="default" htmlColor="var(--colorBlueGray900)" />
                          </DeleteWrapper>
                        </Box>
                        {note.showToCustomers && (
                          <Button
                            type="button"
                            text={
                              loadingNoteLang[index]
                                ? `${t('General.Loading')}...`
                                : isNoteTranslationGenerated(index)
                                ? t('Releases.ViewEditLang')
                                : t('Releases.GenerateLang')
                            }
                            styleType={isNoteTranslationGenerated(index) ? 'Dark' : 'OK'}
                            disabled={loadingNoteLang[index] || !note.titleLang.en.trim()}
                            handleClick={() =>
                              isNoteTranslationGenerated(index)
                                ? toggleNoteTranslationModal(index)
                                : handleGenerateLang(index)
                            }
                            height="40px"
                            style={{ marginTop: '8px' }}
                          />
                        )}

                        <Box marginTop="small">
                          <Field
                            label={t('Releases.FeatureForm.ImageUrls')}
                            description={t('Releases.FeatureForm.ImageUrls.Desc')}>
                            {/* Render existing images */}
                            {note.imageUrls && note.imageUrls.length > 0 && (
                              <Box display="flex" gap="xs" marginBottom="small">
                                {note.imageUrls.map((url, imgIndex) => (
                                  <Box
                                    key={`image-${index}-${imgIndex}`}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gap="xs">
                                    <img
                                      src={url}
                                      alt={`Uploaded ${imgIndex}`}
                                      style={{ height: '50px' }}
                                    />
                                    <Button
                                      styleType="Warning"
                                      height="24px"
                                      width="80px"
                                      text={t('General.Remove')}
                                      type="button"
                                      handleClick={() => {
                                        setFormData({
                                          ...formData,
                                          notes: formData.notes.map((n, i) =>
                                            i === index
                                              ? {
                                                  ...n,
                                                  imageUrls: (n.imageUrls || []).filter(
                                                    (_, ii) => ii !== imgIndex,
                                                  ),
                                                }
                                              : n,
                                          ),
                                        });
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Always have one empty ImageSelect for adding new images */}
                            <ImageSelect
                              key={`imageUrl-${index}-${
                                note.imageUrls ? note.imageUrls.length : 0
                              }`}
                              name={`imageUrl-${index}-${
                                note.imageUrls ? note.imageUrls.length : 0
                              }`}
                              title={t('Releases.FeatureForm.ImageUrls.Add')}
                              setImgIdForAdding={(name, value, url) => {
                                if (url) {
                                  setFormData({
                                    ...formData,
                                    notes: formData.notes.map((n, i) =>
                                      i === index
                                        ? { ...n, imageUrls: [...(n.imageUrls || []), url] }
                                        : n,
                                    ),
                                  });
                                }
                              }}
                            />
                          </Field>

                          <Toggle
                            value={note.showToCustomers}
                            handleChange={(val) => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes[index].showToCustomers = val;
                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}
                            title={t('Releases.ReleaseNoteForm.ShowToCustomers')}
                          />

                          {note.showToCustomers && (
                            <Box marginTop="small" marginBottom="small">
                              <Field
                                label="Notify companies"
                                description="Notify specific companies about this note with a popup in the POS. Useful when a company has requested this feature so they know it's now available.">
                                <Input
                                  list={`companyList-${index}`}
                                  placeholder="Search and select companies..."
                                  type="text"
                                  value={companySearchTerms[index] || ''}
                                  onChange={(e) => handleCompanyInputChange(index, e.target.value)}
                                />
                                <datalist id={`companyList-${index}`}>
                                  {getAvailableCompaniesForNote(index).map((company) => (
                                    <option key={company.id} value={company.name} />
                                  ))}
                                </datalist>

                                {(note.companyIds || []).length > 0 && (
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap="xs"
                                    marginTop="small">
                                    {(note.companyIds || []).map((companyId) => (
                                      <Box
                                        key={companyId}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: 'var(--colorWhite)',
                                          borderRadius: '4px',
                                          border: '1px solid var(--colorBlueGray200)',
                                        }}>
                                        <Paragraph level={4} bottom={0}>
                                          {getCompanyName(companyId)}
                                        </Paragraph>
                                        <Button
                                          text="Remove"
                                          styleType="Warning"
                                          height="24px"
                                          width="80px"
                                          type="button"
                                          handleClick={() =>
                                            removeCompanyFromNote(index, companyId)
                                          }
                                        />
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Field>
                            </Box>
                          )}
                        </Box>
                      </Field>
                    </ReleaseNoteWrapper>
                  ))}
                </Box>

                <Box marginTop="small" display="flex" justifyContent="center">
                  <Button
                    text="Add note"
                    styleType="Dark"
                    width="80px"
                    type="button"
                    handleClick={addNote}
                  />
                </Box>

                <Field
                  label={t('Releases.FeatureForm.ProductTags')}
                  description={t('Releases.FeatureForm.ProductTags.Desc')}>
                  <MultiSelect
                    selected={formData.productTags}
                    list={productTagOptions}
                    placeholder={t('Releases.FeatureForm.ProductTags.Placeholder')}
                    handleChange={(tags) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        productTags: tags as PRODUCT_TAG[],
                      }));
                    }}
                  />
                </Field>

                <Toggle
                  value={formData.triggerModal}
                  handleChange={(val) => {
                    setFormData((prevData) => ({
                      ...prevData,
                      triggerModal: val,
                    }));
                  }}
                  title={
                    t('Releases.TriggerModal') +
                    ' (if the company is notified in the note it will popup regardless of this toggle)'
                  }
                />

                <Box
                  display="flex"
                  flexDirection="column"
                  gap="xs"
                  marginTop="medium"
                  alignItems="center">
                  <Button
                    type="submit"
                    text={
                      loading
                        ? `${t('General.Loading')}...`
                        : release
                        ? t('General.Update')
                        : t('Releases.Publish')
                    }
                    styleType="OK"
                    disabled={loading || formData.notes.length === 0 || !allTranslationsComplete}
                  />
                  {!allTranslationsComplete && (
                    <Paragraph level={4} color="var(--colorBlueGray900)">
                      {t('Releases.FeatureForm.TranslationsNotGenerated')}
                    </Paragraph>
                  )}
                </Box>

                {error && (
                  <Paragraph level={4} color="var(--colorUIRed)">
                    {error}
                  </Paragraph>
                )}
              </Box>
            </form>
          </Box>
        )}
      </Wrapper>

      <Modal isShowing={titleTranslationModal} hide={toggleTitleTranslationModal}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Box display="flex" flexDirection="column" gap="large">
              <Heading level={3}>
                {t('Releases.ReleaseNoteForm.Title')} -{' '}
                {t('Releases.FeatureForm.TitleLang.Translations')}
              </Heading>
            </Box>

            <ScrollableBox display="flex" flexDirection="column" gap="small">
              {Object.entries(formData.titleLang).map(([lang, text]) => (
                <Field
                  label={`${t('Releases.ReleaseNoteForm.Title')} - ${getLanguageName(lang)}`}
                  key={lang}>
                  <Input
                    key={lang}
                    name={`titleLang-${lang}`}
                    placeholder={t('Releases.ReleaseNoteForm.Title.Placeholder')}
                    value={text}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        titleLang: {
                          ...formData.titleLang,
                          [lang]: e.target.value,
                        },
                      });
                    }}
                  />
                </Field>
              ))}
            </ScrollableBox>
          </Box>
        </ModalContentWrapper>
      </Modal>

      {formData.notes.map((note, index) => (
        <Modal
          key={`note-translation-modal-${index}-${note.titleLang.en}`}
          isShowing={noteTranslationModals[index]}
          hide={() => toggleNoteTranslationModal(index)}>
          <ModalContentWrapper>
            <Box display="flex" flexDirection="column" gap="large">
              <Box display="flex" flexDirection="column" gap="large">
                <Heading level={3}>
                  {t('Releases.ReleaseNoteForm.Note')} #{index + 1} -{' '}
                  {t('Releases.FeatureForm.TitleLang.Translations')}
                </Heading>
              </Box>

              <ScrollableBox display="flex" flexDirection="column" gap="small">
                {Object.entries(note.titleLang).map(([lang, text]) => (
                  <Field
                    label={`${t('Releases.ReleaseNoteForm.Note')} - ${getLanguageName(lang)}`}
                    key={lang}>
                    <Input
                      key={lang}
                      name={`noteLang-${index}-${lang}`}
                      placeholder={t('Releases.ReleaseNoteForm.Note.Placeholder')}
                      value={text}
                      onChange={(e) => {
                        const updatedNotes = [...formData.notes];
                        updatedNotes[index] = {
                          ...updatedNotes[index],
                          titleLang: {
                            ...updatedNotes[index].titleLang,
                            [lang]: e.target.value,
                          },
                        };
                        setFormData({
                          ...formData,
                          notes: updatedNotes,
                        });
                      }}
                    />
                  </Field>
                ))}
              </ScrollableBox>
            </Box>
          </ModalContentWrapper>
        </Modal>
      ))}

      <Modal isShowing={confirmDeleteModal.isShowing} hide={confirmDeleteModal.hide}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Heading level={3}>{t('Releases.FeatureForm.ConfirmDelete')}</Heading>

            <Box display="flex" alignItems="center" gap="xs">
              <Button
                styleType="Dark"
                text={t('General.Cancel')}
                handleClick={confirmDeleteModal.hide}
              />
              <Button styleType="Warning" text={t('General.Delete')} handleClick={handleDelete} />
            </Box>
          </Box>
        </ModalContentWrapper>
      </Modal>
    </>
  );
};

export default ReleaseNoteForm;
